/**
 * Human-facing number generation (SRS 0.3, A.8 `number_sequences`).
 *
 * Patterns are tenant-configurable strings with tokens:
 *   {PREFIX}  literal prefix stored on the sequence
 *   {BRANCH}  branch code
 *   {YYYY}    four digit year, {YY} two digit year
 *   {MM}      two digit month, {DD} two digit day
 *   {SEQ:n}   zero-padded counter of width n (default 6)
 *   {CHECK}   a Luhn check digit over every digit before it
 *
 * Example: `{BRANCH}-{YYYY}-{SEQ:6}` renders as `LHR-2026-000123`.
 *
 * The counter itself is allocated by the database (a row lock on
 * `number_sequences`) so numbers are gapless-enough and never duplicated.
 */
/** Every token a pattern may use, with what it renders — the settings screen's legend. */
export const NUMBER_PATTERN_TOKENS = [
    { token: '{SEQ:6}', meaning: 'Running counter, zero-padded to the width given' },
    { token: '{YYYY}', meaning: 'Four-digit year' },
    { token: '{YY}', meaning: 'Two-digit year' },
    { token: '{MM}', meaning: 'Two-digit month' },
    { token: '{DD}', meaning: 'Two-digit day' },
    { token: '{BRANCH}', meaning: 'Branch code, e.g. MAIN' },
    { token: '{CHECK}', meaning: 'Check digit — a typo or two swapped digits is caught when the number is keyed in' },
];
/**
 * Luhn (mod-10) check digit over a run of digits. Catches every single-digit
 * error and nearly every transposition of neighbours, which is what happens
 * when an MRN is read out over the phone.
 */
export function luhnCheckDigit(digits) {
    let sum = 0;
    let double = true;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let d = Number(digits[i]);
        if (double) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        double = !double;
    }
    return String((10 - (sum % 10)) % 10);
}
/** True when `candidate` ends in a Luhn digit that matches the digits before it. */
export function luhnValid(candidate) {
    const digits = candidate.replace(/\D/g, '');
    if (digits.length < 2)
        return false;
    return luhnCheckDigit(digits.slice(0, -1)) === digits.slice(-1);
}
const two = (n) => String(n).padStart(2, '0');
/** Calendar parts of an instant in a zone, falling back to UTC. */
function calendarParts(date, timeZone) {
    if (!timeZone) {
        return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
    }
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const value = (type) => Number(parts.find((part) => part.type === type)?.value ?? '0');
    return { year: value('year'), month: value('month'), day: value('day') };
}
export function renderNumberPattern(pattern, ctx) {
    const { year: y, month, day } = calendarParts(ctx.date, ctx.timeZone);
    const rendered = pattern
        .replace(/\{PREFIX\}/g, ctx.prefix ?? '')
        .replace(/\{BRANCH\}/g, ctx.branchCode ?? '')
        .replace(/\{YYYY\}/g, String(y))
        .replace(/\{YY\}/g, two(y % 100))
        .replace(/\{MM\}/g, two(month))
        .replace(/\{DD\}/g, two(day))
        .replace(/\{SEQ(?::(\d+))?\}/g, (_m, width) => String(ctx.seq).padStart(Number(width ?? 6), '0'));
    // The check digit covers every digit to its left, so it goes last.
    return rendered.replace(/\{CHECK\}/g, (_m, offset) => luhnCheckDigit(rendered.slice(0, offset).replace(/\D/g, '')));
}
/**
 * Where a counter starts. The MRN begins at 100001 so the very first patient
 * gets a six-digit number with no leading zeros — `100001`, not `000001` —
 * which is what a receptionist can read out and a patient can remember.
 */
export const SEQUENCE_START = {
    mrn: 100_001,
};
export function numberPatternProblem(type, pattern) {
    if (!/\{SEQ(?::\d+)?\}/.test(pattern))
        return { code: 'no_counter' };
    const unknown = pattern.match(/\{[^}]*\}/g)?.filter((t) => !/^\{(PREFIX|BRANCH|YYYY|YY|MM|DD|CHECK|SEQ(:\d+)?)\}$/.test(t));
    if (unknown?.length)
        return { code: 'unknown_token', token: unknown[0] };
    if (/[^A-Za-z0-9{}:\-\/._ ]/.test(pattern))
        return { code: 'bad_characters' };
    const reset = SEQUENCE_RESET[type] ?? 'never';
    const hasYear = /\{YYYY\}|\{YY\}/.test(pattern);
    const hasMonth = /\{MM\}/.test(pattern);
    const hasDay = /\{DD\}/.test(pattern);
    if (reset === 'yearly' && !hasYear)
        return { code: 'needs_year' };
    if (reset === 'monthly' && !(hasYear && hasMonth))
        return { code: 'needs_year_month' };
    if (reset === 'daily' && !(hasYear && hasMonth && hasDay) && type !== 'token')
        return { code: 'needs_date' };
    return null;
}
const NUMBER_PATTERN_PROBLEM_TEXT = {
    no_counter: 'The pattern needs a {SEQ} counter',
    unknown_token: 'Unknown token',
    bad_characters: 'Only letters, digits, - / . _ and spaces are allowed around tokens',
    needs_year: 'This counter restarts every year, so the pattern must show the year',
    needs_year_month: 'This counter restarts every month, so the pattern must show the year and month',
    needs_date: 'This counter restarts every day, so the pattern must show the date',
};
export function validateNumberPattern(type, pattern) {
    const problem = numberPatternProblem(type, pattern);
    if (!problem)
        return null;
    if (problem.code === 'unknown_token')
        return `${NUMBER_PATTERN_PROBLEM_TEXT.unknown_token} ${problem.token}`;
    return NUMBER_PATTERN_PROBLEM_TEXT[problem.code];
}
export const DEFAULT_NUMBER_PATTERNS = {
    // Tenant-wide, never resets, six digits from 100001: one lifetime number a
    // patient keeps across every branch.
    mrn: '{SEQ:6}',
    invoice: 'INV-{YY}{MM}-{SEQ:6}',
    receipt: 'RCP-{YY}{MM}-{SEQ:6}',
    token: '{SEQ:3}',
    accession: 'ACC-{YYMMDD}-{SEQ:5}'.replace('{YYMMDD}', '{YY}{MM}{DD}'),
    admission: 'ADM-{YYYY}-{SEQ:6}',
    claim: 'CLM-{YYYY}-{SEQ:6}',
    grn: 'GRN-{YYYY}-{SEQ:6}',
    purchase_order: 'PO-{YYYY}-{SEQ:6}',
    indent: 'IND-{YYYY}-{SEQ:6}',
    journal: 'JV-{YYYY}-{SEQ:6}',
    employee: 'EMP-{SEQ:4}',
    therapy: 'THR-{YYYY}-{SEQ:5}',
    online_order: 'ORD-{YY}{MM}-{SEQ:5}',
    credit_note: 'CN-{YY}{MM}-{SEQ:6}',
    supplier_payment: 'SPY-{YYYY}-{SEQ:6}',
    purchase_return: 'PRT-{YYYY}-{SEQ:6}',
    stock_take: 'STK-{YYYY}-{SEQ:5}',
    stock_adjustment: 'ADJ-{YYYY}-{SEQ:6}',
    er_case: 'ER-{YYYY}-{SEQ:6}',
    estimate: 'EST-{YY}{MM}-{SEQ:6}',
    prescription: 'RX-{YY}{MM}-{SEQ:6}',
    certificate: 'CERT-{YYYY}-{SEQ:6}',
    payer_invoice: 'PTI-{YYYY}-{SEQ:6}',
    blood_donor: 'DNR-{SEQ:6}',
    blood_unit: 'BU-{YYYY}-{SEQ:6}',
    maintenance_ticket: 'MT-{YYYY}-{SEQ:5}',
};
/**
 * Tokens reset the counter at different cadences. `token` resets daily per
 * doctor/department, invoices monthly, MRN never.
 */
export const SEQUENCE_RESET = {
    mrn: 'never',
    invoice: 'monthly',
    receipt: 'monthly',
    token: 'daily',
    accession: 'daily',
    admission: 'yearly',
    claim: 'yearly',
    grn: 'yearly',
    purchase_order: 'yearly',
    indent: 'yearly',
    journal: 'yearly',
    employee: 'never',
    therapy: 'yearly',
    online_order: 'monthly',
    credit_note: 'monthly',
    supplier_payment: 'yearly',
    purchase_return: 'yearly',
    stock_take: 'yearly',
    stock_adjustment: 'yearly',
    er_case: 'yearly',
    estimate: 'monthly',
    prescription: 'monthly',
    certificate: 'yearly',
    payer_invoice: 'yearly',
    blood_donor: 'never',
    blood_unit: 'yearly',
    maintenance_ticket: 'yearly',
};
/** The scope key a counter resets against, e.g. `2026-08-26` for a daily token. */
export function sequencePeriodKey(type, date, timeZone) {
    const { year, month, day } = calendarParts(date, timeZone);
    switch (SEQUENCE_RESET[type] ?? 'never') {
        case 'daily':
            return `${year}-${two(month)}-${two(day)}`;
        case 'monthly':
            return `${year}-${two(month)}`;
        case 'yearly':
            return String(year);
        default:
            return 'all';
    }
}
//# sourceMappingURL=numbering.js.map